import {
  createResourceInfiniteQueryAtom,
  createResourceQueryAtom,
  defineResource,
  defineResourceInfiniteQuery,
  defineResourceQuery,
} from "#/shared/lib/resource";

import { getKnowledgeOverview } from "../api/get-knowledge-overview";
import {
  getKnowledgeProducts,
  type KnowledgeProductFilters,
} from "../api/get-knowledge-products";
import { getKnowledgeQuestions } from "../api/get-knowledge-questions";

const knowledgeResource = defineResource("knowledge");

const knowledgeOverviewQuery = defineResourceQuery({
  key: () => [],
  name: "overview",
  queryFn: () => getKnowledgeOverview(),
  resource: knowledgeResource,
  staleTime: 60_000,
});
export const knowledgeOverviewAtom = createResourceQueryAtom(
  knowledgeOverviewQuery,
  () => undefined,
);

const knowledgeProductsQuery = defineResourceInfiniteQuery({
  key: (filters: KnowledgeProductFilters) => [
    filters.q ?? "",
    filters.category ?? "",
  ],
  name: "products",
  queryFn: (filters: KnowledgeProductFilters, cursor) =>
    getKnowledgeProducts(filters, cursor),
  resource: knowledgeResource,
  staleTime: 30_000,
});

function readProductsCacheKey(filters: KnowledgeProductFilters): string {
  return JSON.stringify([filters.q, filters.category]);
}

const productAtomsByFilters = new Map<
  string,
  ReturnType<typeof createProductsAtom>
>();

function createProductsAtom(filters: KnowledgeProductFilters) {
  return createResourceInfiniteQueryAtom(knowledgeProductsQuery, () => filters);
}

/**
 * Products 标签的读模型，按搜索与筛选组合各留一个 atom：改回上一组条件能直接命中缓存。
 * 组合来自少量的搜索词与两个筛选项，atom 建了就一直留着；不用 jotai 的 atomFamily
 * 是因为它已标记废弃，替代品在仓库里没有依赖（与 home-metrics 同一处理）。
 */
export function knowledgeProductsAtom(filters: KnowledgeProductFilters) {
  const cacheKey = readProductsCacheKey(filters);
  const existing = productAtomsByFilters.get(cacheKey);

  if (existing !== undefined) {
    return existing;
  }

  const created = createProductsAtom(filters);
  productAtomsByFilters.set(cacheKey, created);

  return created;
}

const knowledgeQuestionsQuery = defineResourceInfiniteQuery({
  key: () => [],
  name: "questions",
  queryFn: (_input: undefined, cursor) => getKnowledgeQuestions(cursor),
  resource: knowledgeResource,
  staleTime: 0,
});
export const knowledgeQuestionsAtom = createResourceInfiniteQueryAtom(
  knowledgeQuestionsQuery,
  () => undefined,
);
